import { LiveChatWidget } from '@livechat/widget-react';
import { FC, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { LIVE_CHAT_LICENSE } from '../constants/common';
import { State } from '../interface';

interface Props {
	visibility: 'maximized' | 'minimized';
	handleChange: (visibility: 'maximized' | 'minimized') => void;
}

const LiveChatHash = '#chat';

const LiveChatWidgetComponent: FC<Props> = ({ visibility, handleChange }) => {
	const [loginData, setLoginData] = useState<any>();
	const login = useSelector<State, any>((state) => state.login.userDetails);
	const isUserVip = typeof loginData?.vipStatusData === 'number';

	const syncingRef = useRef(false);

	useEffect(() => {
		if (login) setLoginData(login);
	}, [login]);

	useEffect(() => {
		let rafId: number;

		const syncFromHash = () => {
			if (syncingRef.current) return;

			const isHashChat = window.location.hash === LiveChatHash;

			syncingRef.current = true;

			if (isHashChat && visibility !== 'maximized') {
				rafId = window.requestAnimationFrame(() => {
					handleChange('maximized');
					syncingRef.current = false;
				});
			} else if (!isHashChat && visibility !== 'minimized') {
				handleChange('minimized');
				rafId = window.requestAnimationFrame(() => {
					syncingRef.current = false;
				});
			} else {
				syncingRef.current = false;
			}
		};

		syncFromHash();
		window.addEventListener('hashchange', syncFromHash);

		return () => {
			window.removeEventListener('hashchange', syncFromHash);
			cancelAnimationFrame(rafId);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (syncingRef.current) return;

		const isHashChat = window.location.hash === LiveChatHash;

		if (visibility === 'maximized' && !isHashChat) {
			window.history.pushState(null, '', LiveChatHash);
		} else if (visibility === 'minimized' && isHashChat) {
			if (window.history.length > 1) {
				window.history.back();
			} else {
				window.history.replaceState(null, '', window.location.pathname + window.location.search);
			}
		}
	}, [visibility]);

	const handleVisibilityChange = (state: any) => {
		const newVisibility = state?.visibility;
		if (!newVisibility || newVisibility === visibility) return;
		if (syncingRef.current) return;

		syncingRef.current = true;

		if (newVisibility === 'maximized') {
			if (window.location.hash !== LiveChatHash) {
				window.history.pushState(null, '', LiveChatHash);
			}
		} else {
			if (window.location.hash === LiveChatHash) {
				if (window.history.length > 1) {
					window.history.back();
				} else {
					window.history.replaceState(null, '', window.location.pathname + window.location.search);
				}
			}
		}

		handleChange(newVisibility);
		setTimeout(() => {
			syncingRef.current = false;
		}, 100);
	};

	return (
		<LiveChatWidget
			license={LIVE_CHAT_LICENSE}
			customerEmail={loginData?.email}
			customerName={loginData?.firstName}
			sessionVariables={{ vip: isUserVip ? 'true' : 'false' }}
			visibility={visibility}
			onVisibilityChanged={handleVisibilityChange}
		/>
	);
};

export default LiveChatWidgetComponent;
